import type { Translations } from './types'

export const en: Translations = {
  a11y: {
    openSupportChat: 'Open support chat',
    closeSupportChat: 'Close support chat',
    supportUnread: (n: number) => (n === 1 ? '1 new reply' : `${n} new replies`),
    toggleTheme: 'Toggle theme',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    courseVideos: 'Course videos',
    previousSlide: 'Previous',
    nextSlide: 'Next',
    goToSlide: (n: number) => `Go to slide ${n}`,
  },
  support: {
    title: 'Chat with us',
    subtitle: 'Report a problem or ask a question',
    greeting: 'Hi! What can we help you with?',
    placeholder: 'Write a message…',
    send: 'Send',
    emailPromptTitle: 'Want our reply by email too?',
    emailPromptBody:
      "Nobody has picked this up yet. Leave an address and we'll reply there as well, so you don't have to wait here.",
    emailPromptSubmit: 'Add',
    emailPromptDismiss: 'No thanks',
    emailPromptDone: (email: string) => `Thanks — we'll reply to ${email} as well.`,
    emailLabel: 'Email address',
    emailPlaceholder: 'you@company.com',
    privacyNote: 'We use it only to answer you. See our',
    privacyLink: 'Privacy Policy',
    pendingBadge: 'Not sent yet',
    sending: 'Sending…',
    retry: 'Retry',
    validationEmail: 'Please enter your email address',
    validationEmailInvalid: 'Please enter a valid email address',
    validationMessage: 'Please write a message first',
    errorGeneric: 'Something went wrong. Please try again.',
    errorRateLimited: 'Too many messages. Please try again in a little while.',
    errorCaptcha: 'Please complete the security check',
    errorSessionExpired: 'This conversation has expired.',
    threadClosed: 'This conversation is closed.',
    startNew: 'Start a new conversation',
    endConversation: 'End this conversation',
    endConfirm: 'This clears the conversation from this browser. Continue?',
    emailedYou: (email: string) => `We'll also email you at ${email}`,
    staffLabel: 'EVTIMIA',
    youLabel: 'You',
    imagesAfterFirstMessage: 'Write your first message in text; you can add images after that.',
  },
  chat: {
    moreActions: 'Message actions',
    reply: 'Reply',
    react: 'React',
    copyText: 'Copy text',
    copied: 'Copied',
    edit: 'Edit',
    unsend: 'Unsend',
    unsendConfirm: 'Remove this message for everyone?',
    pin: 'Pin',
    unpin: 'Unpin',
    forward: 'Forward',
    history: 'History',
    edited: 'edited',
    removed: 'This message was removed',
    replyingTo: (name: string) => `Replying to ${name}`,
    editing: 'Editing message',
    cancel: 'Cancel',
    pinnedCount: (n: number) => (n === 1 ? 'Pinned message' : `${n} pinned messages`),
    forwarded: 'Forwarded',
    forwardTitle: 'Forward to…',
    forwardSearch: 'Search conversations',
    forwardDone: (name: string) => `Forwarded to ${name}`,
    forwardNone: 'No conversations found.',
    historyTitle: 'Message history',
    historyEdited: 'Before an edit',
    historyUnsent: 'Removed',
    historyEmpty: 'No earlier versions.',
    reactedBy: (names: string) => `Reacted: ${names}`,
    mentionNone: 'Nobody in this conversation matches',
    actionFailed: 'That didn’t work. Try again.',
    you: 'You',
    notLoaded: 'That message is too far back to show.',
    fileRemoved: 'File removed',
    image: 'Image',
    seen: 'Seen',
    seenBy: (names: string) => `Seen by ${names}`,
    typing: (name: string) => `${name} is typing…`,
    typingSeveral: 'Several people are typing…',
    online: 'Online',
    lastSeen: (when: string) => `Last seen ${when}`,
    teamOnline: 'The team is online',
    teamTyping: 'The team is typing…',
    searchNoResults: 'No messages match.',
    closeSearch: 'Close search',
    emoji: 'Emoji',
    moreOptions: 'More options',
    moreReactions: 'More reactions',
    emojiSearch: 'Search emoji',
    newMessage: 'New message',
    newSupportMessage: 'New support message',
    mentionedYou: 'You were mentioned',
    newFromTeam: 'New message from EVTIMIA',
  },
  chatHub: {
    searchEverything: 'Search people, groups, messages and files',
    searchInbox: 'Search visitors, messages and files',
    searchConversation: 'Search this conversation',
    sectionPeople: 'People',
    sectionGroups: 'Groups',
    sectionMessages: 'Messages',
    sectionFiles: 'Files',
    sectionVisitors: 'Visitors',
    noMatches: 'Nothing matches that.',
    typeMore: 'Keep typing to search messages and files.',
    newChat: 'New',
    newGroup: 'New group',
    groupName: 'Group name',
    groupNamePlaceholder: 'e.g. Launch team',
    groupMembers: 'Members',
    groupAddPeople: 'Add colleagues',
    groupCreate: 'Create group',
    groupSave: 'Save',
    groupInfo: 'Group info',
    groupLeave: 'Leave group',
    groupLeaveConfirm: 'Leave this group? You will stop seeing its messages.',
    groupMemberCount: (n: number) => (n === 1 ? '1 member' : `${n} members`),
    groupCreatedBy: (name: string) => `Created by ${name}`,
    groupManagedBy: 'Only the person who created this group, or an admin, can rename it or change its members.',
    groupRemoveMember: (name: string) => `Remove ${name}`,
    groupNeedsName: 'Give the group a name',
    groupNeedsMember: 'Add at least one colleague',
    groupFailed: 'The group could not be saved. Please try again.',
    groupYou: 'You',
    pushSection: 'This device',
    pushSwitch: 'Notifications on this device',
    pushNeedsInstall: 'On iPhone and iPad, notifications work only once EVTIMIA is on your home screen. In Safari, tap Share, then “Add to Home Screen”, then open EVTIMIA from the new icon and come back here.',
    pushOnInHomeScreenApp: 'Notifications are already on in the EVTIMIA app on your home screen. Open EVTIMIA from its icon to change them.',
    pushBlocked: 'Notifications are blocked for this site. Allow them in your browser’s or phone’s settings for this site, then reload this page.',
    pushUnsupported: 'Notifications can’t be turned on in this browser. Use an up-to-date Chrome, Edge, Firefox or Safari, or another browser that supports them.',
    pushInApp: 'You’re viewing this inside {app}, which can’t show notifications. Open its menu (⋯ or ⋮) and choose to open the page in your browser, then turn them on there.',
    pushInAppUnknown: 'another app',
    pushDeclined: 'Your browser did not allow notifications, so they stay off.',
    pushFailed: 'Notifications could not be turned on. Please try again.',
    nudgeTitle: 'Get notified about replies?',
    nudgeBody: 'Turn on notifications to hear about new messages, even when EVTIMIA is closed.',
    nudgeIos: 'On iPhone and iPad, notifications need EVTIMIA on your home screen: tap Share, then “Add to Home Screen”, and open EVTIMIA from the new icon.',
    nudgeTurnOn: 'Turn on notifications',
    nudgeDismiss: 'Dismiss',
    nudgeOn: 'Notifications are on for this device.',
    categories: {
      chat: { label: 'Direct and group messages' },
      mentions: { label: '@mentions', description: 'When someone mentions you, even if that chat is switched off above.' },
      clientMessages: { label: 'Client conversations' },
      workChats: { label: 'Project and task chats' },
      support: { label: 'Support inbox', description: 'Visitors writing in the website chat. Once a thread is assigned, only its owner hears about it.' },
      taskReview: { label: 'Tasks' },
      reminders: { label: 'Reminders' },
      teamMessages: { label: 'Messages from the team' },
      deliveries: { label: 'New deliveries' },
    },
    saveFailed: 'That change could not be saved. Please try again.',
    newsletterTitle: 'Newsletter',
    newsletterLabel: 'Send me the EVTIMIA newsletter',
    newsletterDescription: 'Occasional news, guides and offers from EVTIMIA. You can unsubscribe here at any time.',
    newsletterSince: (date: string) => `Subscribed since ${date}.`,
    newsletterUnsubscribeTitle: 'Unsubscribe from the newsletter?',
    newsletterUnsubscribeBody: 'You will stop receiving news, guides and offers from EVTIMIA. You can subscribe again here at any time.',
    newsletterUnsubscribeConfirm: 'Unsubscribe',
    newsletterUnsubscribeCancel: 'Stay subscribed',
    mailingTitle: 'Mailing list',
    mailingDescription: 'Download a fresh copy before each send, so people who unsubscribed are left out.',
    mailingCount: (n: number) => (n === 1 ? '1 subscriber' : `${n} subscribers`),
    mailingDownload: 'Download CSV',
    mailingEmpty: 'Nobody has subscribed yet.',
    mailingError: 'The mailing list could not be loaded.',
    mailingColumns: { name: 'Name', email: 'Email', company: 'Company', role: 'Role', since: 'Subscribed' },
    mailingRoles: { admin: 'Admin', employee: 'Employee', client: 'Client' },
  },
  chatFiles: {
    attach: 'Attach photos or files',
    recordVoice: 'Record a voice note',
    stopRecording: 'Stop and add',
    cancelRecording: 'Discard recording',
    recording: 'Recording…',
    micDenied: 'Microphone access was blocked. Allow it in your browser to record.',
    micUnsupported: 'This browser cannot record audio.',
    blocked: 'That file type can’t be sent (programs and web pages are blocked).',
    tooLarge: 'Too large: documents up to 50 MB, voice notes 20 MB, videos 100 MB.',
    tooLargeSupport: 'Too large: files in this chat can be up to 25 MB.',
    tooLong: 'Voice notes can be at most 5 minutes.',
    tooMany: (max: number) => `You can attach up to ${max} files to one message.`,
    voiceNote: 'Voice note',
    video: 'Video',
    file: 'File',
    open: (name: string) => `Open ${name}`,
    download: 'Download',
    remove: 'Remove attachment',
  },
  chatImages: {
    attach: 'Add images',
    remove: 'Remove image',
    dropHere: 'Drop images to add them',
    preparing: 'Preparing image…',
    tooMany: (max: number) => `You can add up to ${max} images to one message.`,
    unsupported: 'That file isn’t an image we can send. Try a JPG, PNG, WebP or GIF.',
    tooLarge: 'That image is too large (over 10 MB).',
    uploadFailed: 'The images couldn’t be uploaded. Nothing was sent. Try again.',
    open: (n: number, total: number) => (total > 1 ? `Open image ${n} of ${total}` : 'Open image'),
    alt: 'Image',
    copy: 'Copy image',
    copied: 'Copied',
    copyFailed: 'Your browser blocked copying. Use Download instead.',
    download: 'Download',
    downloadFailed: 'The download failed.',
    previous: 'Previous image',
    next: 'Next image',
    loadFailed: 'Image unavailable',
    viewerTitle: 'Image viewer',
    close: 'Close',
  },
  nav: {
    home: 'Home',
    services: 'Services',
    work: 'Portfolio',
    about: 'About',
    contact: 'Contact',
    login: 'Login',
    signUp: 'Sign up',
    account: 'Account',
    clientPortal: 'Client portal',
    crmDashboard: 'CRM dashboard',
  },
  hero: {
    title: 'We do the digital work, so that you can do yours.',
    subtitle: 'A digital marketing agency combining strategic growth with responsible technology and AI.',
    ctaBook: 'Book a Consultation',
    ctaServices: 'Explore Services',
  },
  closingCta: {
    eyebrow: "Let's talk",
    servicesLabel: 'Our services',
    cardCta: 'View service',
    heading: 'Tell us how we can help.',
    body: 'A short call is usually enough to tell whether we are the right fit. No pitch deck, no obligation.',
    primary: 'Book a Consultation',
    secondary: 'Send a message',
  },
  whatIsEvtimia: {
    eyebrow: 'What is EVTIMIA?',
    statement: 'A marketing team that starts from your business goals.',
    intro: 'We plan, create and automate your online presence around what you want to achieve — so every post, ad and page has a reason to exist.',
    photoCaption: 'The EVTIMIA studio, Cluj-Napoca',
    facts: [
      { title: 'Goals first', body: 'We start from your business objectives, then choose the channels and content that serve them.' },
      { title: 'Strategy, content, consulting', body: 'One team that plans the direction, makes the work and stays with you as it runs.' },
      { title: 'Marketing + technology', body: 'Digital marketing, AI and automation together, built to keep working long after launch.' },
    ],
    cta: 'Learn more about us',
    photoAlt: 'Microphones, headphones and a tablet on a wooden desk in the EVTIMIA studio',
  },
  socialProof: {
    trustedBy: 'Trusted by',
  },
  booking: {
    heading: 'Book a Consultation',
    subtitle: 'Schedule a call with our team.',
    pickTime: 'Pick a time',
    noSlots: 'No times available on this day.',
    selectedPrefix: 'Selected',
    selectedNote: 'Add your details below and we will send the calendar invite straight away.',
    timezoneNote: 'Times shown in Bucharest time (EET/EEST).',
    localTimeNote: 'That is {time} your time.',
    slotsError: 'Could not load available times. Please try again shortly.',
    nameLabel: 'Your name',
    namePlaceholder: 'Ana Popescu',
    emailLabel: 'Email',
    emailPlaceholder: 'ana@company.com',
    noteLabel: 'What would you like to discuss? (optional)',
    notePlaceholder: 'A sentence or two helps us prepare.',
    submit: 'Book this time',
    submitting: 'Booking…',
    missingHint: 'Still needed: {fields}',
    securityCheck: 'the security check',
    successTitle: 'Your call is booked',
    successBody:
      'Check your inbox — we have sent a calendar invite with the Google Meet link for {when}.',
    slotTakenError: 'Sorry, that time was just taken. Please pick another.',
    dailyLimitError: 'Booking is closed for today. Please try again tomorrow, or email us at hello@evtimia.com.',
    genericError: 'We could not complete the booking. Please try again.',
  },
  footer: {
    description: 'A Social Enterprise combining strategic digital marketing with responsible technology and AI, growing businesses while making digital education accessible.',
    companyDetails: 'EVTIMIA SRL',
    quickLinks: 'Quick Links',
    allRights: 'All rights reserved.',
    privacyPolicy: 'Privacy Policy',
    terms: 'Terms & Conditions',
    cookiePolicy: 'Cookie Policy',
    sal: 'SAL',
  },
  cookieBanner: {
    cookieSettings: 'Cookie Settings',
    title: 'We value your privacy',
    description: 'We use cookies to ensure our website functions properly. With your consent, we may also use cookies to understand how you interact with our site and to support our marketing efforts. You can manage your preferences at any time.',
    acceptAll: 'Accept All',
    rejectNonEssential: 'Reject Non-Essential',
    managePreferences: 'Manage Preferences',
    strictlyNecessary: 'Strictly Necessary',
    strictlyNecessaryDesc: 'Required for core website functionality, security, and fraud prevention. These cookies cannot be disabled.',
    analytics: 'Analytics & Performance',
    analyticsDesc: 'Help us understand how visitors interact with the website by collecting anonymised usage data.',
    marketing: 'Marketing & Communication',
    marketingDesc: 'Used to measure the effectiveness of our campaigns and deliver relevant content.',
    savePreferences: 'Save Preferences',
    footerNote: 'You can change or withdraw your consent at any time. See our',
    cookiePolicyLink: 'Cookie Policy',
    privacyPolicyLink: 'Privacy Policy',
    footerAnd: 'and',
    footerSuffix: 'for details.',
  },
  serviceCard: {
    popular: 'Popular',
    viewDetails: 'View details',
  },
  serviceDetail: {
    addOns: 'Available Add-Ons',
    requestOffer: 'Request a Custom Offer',
    backToServices: 'All services',
    otherServices: 'Other Services',
    ctaHeading: 'Ready to get started?',
    ctaSubtitle: 'Tell us about your business and we will put together a plan that fits.',
    pricingHeading: 'Subscription & Pricing',
    pricingDescription: 'One monthly subscription that covers strategy, production and management.',
    planCta: 'Apply for this subscription',
    vettingNote: 'No payment is taken upfront. You fill in a short brief, our team reviews the information, and the subscription is only invoiced after we accept the project.',
    whatsIncluded: "What's included",
    servicesPricingHeading: 'Services & Pricing',
    servicesPricingDescription: 'Pick the service you need — each one is scoped, quoted and delivered on its own.',
    mainServicesHeading: 'Core Web Projects',
    maintenanceHeading: 'Ongoing Maintenance & Add-Ons',
    itemCta: 'Request this service',
    serviceVettingNote: 'No payment is taken upfront. You fill in a short brief, our team reviews the information, and the service is only invoiced after we accept the project.',
  },
  orderIntro: {
    orderOnline: 'Order online',
    included: "What's included",
    revisions: n => `${n} revision ${n === 1 ? 'round' : 'rounds'}`,
    delivery: n => `Delivery in about ${n} working ${n === 1 ? 'day' : 'days'}`,
    rushDelivery: n => `${n} with rush delivery`,
    price: 'Price',
    priceFrom: price => `From ${price} + VAT`,
    quoteOnly: days => `No price up front: you get a written quote within ${days} working ${days === 1 ? 'day' : 'days'}.`,
    prepare: 'Have these ready',
    needsQuote: 'Needs a quote',
    start: 'Start',
    continueDraft: 'Continue your order',
    accountNote: 'You’ll create a free account first. That is where you follow the order and receive the files.',
    close: 'Close',
  },
  servicesPage: {
    tagline: 'Digital Marketing · AI · Automation',
    heroTitle: 'Marketing Systems. AI. Automation. Social Impact.',
    heroSubtitle: 'We grow businesses through structured digital marketing, artificial intelligence, and automation — with a commitment to social impact and digital education.',
    ctaPlan: 'Get a Custom Plan',
    gridTitle: 'Our Services',
    gridSubtitle: 'End-to-end digital marketing, structured for clarity and measurable results.',
    faqTitle: 'Frequently Asked Questions',
    faq: [
      {
        question: 'Why Choose EVTIMIA?',
        answer: "EVTIMIA was built for entrepreneurs who want a clear, efficient, and well-thought-out online presence. We work in a structured way, starting from business objectives and adapting solutions to each company's context.",
      },
      {
        question: 'How Does EVTIMIA Support Business Growth?',
        answer: 'We combine digital marketing, technology, and automation to deliver solutions that work long-term and can be easily managed by our clients. Our focus on strategy, content, and applied consultancy ensures that every action is tied to measurable business outcomes — not vanity metrics.',
      },
      {
        question: 'What Makes EVTIMIA Different from Other Agencies?',
        answer: 'We do not limit ourselves to delivering marketing services. We believe technology has real value only when it is understood, which is why we consistently invest in digital education and knowledge sharing. We provide free resources and dedicated training for the community, with the goal of contributing to the reduction of the digital divide.',
      },
    ],
    ctaBook: 'Book a Consultation',
  },
  aboutPage: {
    heroTitle: 'Our Story',
    heroSubtitle1: 'EVTIMIA is a social enterprise in digital marketing that combines modern technology with community responsibility.',
    teamTitle: 'Meet the Team',
    teamSubtitle: 'We are not just an agency, we are your partners in digital transformation.',
    missionTitle: 'Mission',
    missionText: 'We guide entrepreneurs in using technology and artificial intelligence intelligently and responsibly, to grow their businesses and make better-informed decisions.',
    visionTitle: 'Vision',
    visionText: 'We\'re building a space where marketing, artificial intelligence, and education complement each other naturally, so that technology is used consciously and responsibly, with impact on both business development and people\'s lives and communities.',
    valuesTitle: 'Our Values',
    values: [
      { title: 'Results', text: "We don't measure success in likes, but in sales and growth." },
      { title: 'Transparency', text: 'Clear reporting, open communication, no unrealistic promises. You always know where your money goes and what results you get.' },
      { title: 'Social impact', text: 'We believe in accessible technology. We educate the local community with free resources and training, and help close the digital gap.' },
      { title: 'Innovation', text: 'We use modern technology with a clear purpose: efficiency, time saved and better results.' },
    ],
    studioTitle: 'Our Team. Our Studio. Our Way of Working.',
    heroEyebrow: 'About EVTIMIA',
    approachEyebrow: 'Our approach',
    approachTitle: 'Clear communication, measurable results.',
    approachText:
      "We set out to help clients build clear, coherent digital communication aimed at measurable results. We use artificial intelligence and automation to bring structure, efficiency and more control to marketing, so businesses can make informed decisions and adapt more easily in a digital world that keeps changing.\n\nWhat sets us apart is that we don't stop at delivering marketing services: technology has real value when it is understood, not simply handed off, so we keep investing in digital education and in sharing what we know.",
    missionVisionEyebrow: 'What drives us',
    founderEyebrow: 'Our founder',
    founderQuote:
      "I became an entrepreneur in 2014, when I learned marketing out of necessity, to support my first business. Experience showed me that a healthy business can't grow without well-thought-out marketing, able to support sales and real results. Moving from my own projects to working with other entrepreneurs gave me an important advantage: a first-hand understanding of business needs, constraints and the pressure of decisions. That perspective lets me see marketing beyond execution, as part of a business's growth strategy.\n\nMarketing has become a constant passion, applied both in my own businesses and in my work with partners. In every project I have pursued a personal approach, built on collaboration and trust. At EVTIMIA we work with clients as partners, not as a classic agency, because I am convinced that results come when marketing is truly connected to the business.",
    founderName: 'Ioana Sucală',
    founderRole: 'Founder of EVTIMIA',
    founderPhotoAlt: 'Ioana Sucală, founder of EVTIMIA, at her desk in the studio',
  },
  workPage: {
    tagline: 'Case Studies · Client Work · Social Impact',
    heroTitle: 'The Work Behind the Results.',
    heroSubtitle: 'From our own social enterprise platform to the brands we grow every day — here is what we build, and who we build it for.',
    featuredLabel: 'Our Own Project',
    visitSite: 'Visit the site',
    studioPhotoAlt: 'In the EVTIMIA studio',
    clientCardsHeading: 'Their Wins, Our Work',
    clientCardsSubheading: 'Tap a card to see their story.',
    screenshotsLabel: 'Selected work',
    galleryClose: 'Close',
    clientVisitSite: 'Visit their site',
    clientFollowers: 'followers',
    clientOpenProfile: 'Open profile',
    clientSoundOn: 'Turn sound on',
    clientSoundOff: 'Mute',
    clientServicesTitle: 'Services behind these results',
    clientGalleryTitle: 'Behind the scenes',
    clientPostsTitle: 'Posts we made',
    clientPhotoOpen: 'Open photo {n} of {total}',
    clientPhotoClose: 'Close photo',
    clientPhotoPrevious: 'Previous photo',
    clientPhotoNext: 'Next photo',
    galleryVisit: 'See the original',
    galleryOpenOn: 'Open on',
    galleryPlatforms: { youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram', facebook: 'Facebook', linkedin: 'LinkedIn' },
    featuredBlurb: 'Our own social enterprise platform: a free digital education library, in Romanian, built for the people technology usually leaves behind.',
    videosTitle: 'Watch a few of the courses',
    videosSubtitle: 'Short, jargon-free lessons from the Ghid.Online library.',
    watchLabel: 'Watch on Ghid.Online',
    ghidCourses: 'Courses',
    ghidSeeAll: 'See all',
    ghidTracks: 'Learning tracks',
    ghidCourseOne: '1 course',
    ghidCourseMany: '{n} courses',
    ghidPrevious: 'Previous course',
    ghidNext: 'Next course',
    ghidOfferTitle: 'The internet, explained in short videos.',
    ghidTopics: [
      { title: 'Online safety', hint: 'Scams, fake links, 2FA' },
      { title: 'AI & technology', hint: 'Deepfakes, AI, social media' },
      { title: 'Digital skills', hint: 'Health info, smart searching' },
    ],
    ghidAudience: 'In Romanian · for every age',
    ghidPrice: '0 lei',
    ghidPriceNote: 'a month. And a year. And forever.',
    ghidFreeQuips: [
      'No card. No trial. No catch.',
      'Cheaper than a coffee. A lot cheaper.',
      'Nothing online is free. Except this.',
    ],
    videos: [
      { youtubeId: 'TXlDoqch8D0', title: 'Introducing the Digital Library', category: 'Digital skills' },
      { youtubeId: '9VZwq6LXMpc', title: 'Why two-factor authentication matters', category: 'Online safety' },
      { youtubeId: 'emfsIoDhLWU', title: 'How to spot fake links on Facebook', category: 'Online safety' },
      { youtubeId: '4R64R1SWd1Q', title: 'When online romance turns into a scam', category: 'Online safety' },
      { youtubeId: '3oFQ3fVjcIw', title: 'How deepfakes are used in advertising', category: 'Technology' },
      { youtubeId: 'SgOjdUvz6Sc', title: 'AI and social media: how they shape what we see', category: 'Technology' },
      { youtubeId: 'mwVEqwNxjjk', title: 'The AI caricature trend: harmless fun or data collection?', category: 'Technology' },
      { youtubeId: 'QfZeD4bZCfQ', title: 'Using the internet well for medical decisions', category: 'Digital skills' },
    ],

    ctaHeading: 'Want results like these?',
    ctaSubtitle: 'Tell us where your business is today and where you want it to go.',
    ctaButton: 'Start a Conversation',
  },
  loginPage: {
    signInTitle: 'Welcome back',
    signInSubtitle: 'Sign in to access your EVTIMIA client portal.',
    signUpTitle: 'Create your account',
    signUpSubtitle: 'Set up an account to track your projects and orders.',
    firstNameLabel: 'First name',
    firstNamePlaceholder: 'Your first name',
    lastNameLabel: 'Surname',
    lastNamePlaceholder: 'Your surname',
    emailLabel: 'Email',
    emailPlaceholder: 'you@company.com',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    confirmPasswordLabel: 'Confirm password',
    confirmPasswordPlaceholder: 'Repeat your password',
    forgotPassword: 'Forgot password?',
    signInButton: 'Sign in',
    signUpButton: 'Create account',
    noAccount: "Don't have an account?",
    signUpLink: 'Sign up',
    haveAccount: 'Already have an account?',
    signInLink: 'Sign in',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    comingSoonTitle: 'Client portal coming soon',
    comingSoonMessage: 'Accounts are not open just yet. We are building the portal where you will follow your projects, orders and reports in one place. In the meantime, get in touch and we will help you directly.',
    backToSite: 'Back to the site',
    validationFirstName: 'Please enter your first name',
    validationLastName: 'Please enter your surname',
    validationEmail: 'Please enter your email',
    validationEmailInvalid: 'Please enter a valid email address',
    validationPassword: 'Please enter your password',
    validationPasswordShort: 'Password must be at least 8 characters',
    validationPasswordRules: 'Password is missing: {rules}',
    validationPasswordWeak: 'This password is too weak or easy to guess. Please choose a stronger one.',
    validationPasswordLeaked: 'This password has appeared in a known data breach. Please choose a different one.',
    validationEmailTaken: 'This email already has an account. Sign in instead, or reset your password.',
    validationEmailRejected: 'This email address was rejected. Please check it and try again.',
    validationRateLimit: 'Too many attempts. Please wait a moment and try again.',
    validationConfirm: 'Please confirm your password',
    validationConfirmMismatch: 'Passwords do not match',
    pwRuleMinLength: 'At least 8 characters',
    pwRuleUpper: 'One uppercase letter',
    pwRuleLower: 'One lowercase letter',
    pwRuleNumber: 'One number',
    pwRuleSymbol: 'One symbol',
    pwRuleTooLong: 'Passwords cannot be longer than 72 characters. Please shorten it.',
    missingFields: 'Still needed: {fields}',
    fieldFirstName: 'first name',
    fieldLastName: 'surname',
    fieldEmail: 'email',
    fieldPassword: 'password',
    fieldConfirmPassword: 'password confirmation',
    rememberEmail: 'Remember my email',
    alreadyHaveAccount: "Didn't get an email? You may already have an account.",
    emailTakenTitle: 'This email already has an account',
    emailTakenMessage: 'Sign in with your existing account, or reset your password if you have forgotten it.',
    goToSignIn: 'Sign in instead',
    goToReset: 'Reset your password',
    signInError: 'Incorrect email or password. Please try again.',
    signUpError: 'We could not create your account. Please try again.',
    resetTitle: 'Reset your password',
    resetSubtitle: "Enter your email and we'll send you a reset link.",
    resetSent: 'Check your email',
    resetSentMessage: "If an account exists for that email, we've sent a link to reset your password.",
    resetButton: 'Send reset link',
    resetError: 'We could not send the reset link. Please try again in a moment.',
    validationPasswordRejected: 'This password does not meet the requirements. Please choose a longer or more complex one.',
    backToSignIn: 'Back to sign in',
    checkEmailTitle: 'Confirm your email',
    checkEmailMessage: "We've sent a confirmation link to your email. Click it to finish creating your account.",
    onFileTitle: 'We already have you on file',
    onFileMessage:
      "Your account is already set up with us. We've sent an activation link to your email: open it to choose your password and see everything we've prepared for you.",
    confirmLinkInvalidTitle: 'Confirmation link expired or invalid',
    confirmLinkInvalidMessage: 'This email confirmation link is no longer valid. Sign up again to receive a new one.',
  },
  confirmEmailPage: {
    title: 'Confirm your email',
    subtitle: 'Press the button below to confirm your email address.',
    button: 'Confirm',
    pendingTitle: 'Almost there',
    pendingMessage: 'Now confirm using the link sent to your other email address.',
    invalidTitle: 'This link has expired',
    invalidMessage: 'This link is older than 30 minutes or was already used. Request a new one and try again.',
    home: 'Back to home',
  },
  resetPasswordPage: {
    title: 'Choose a new password',
    subtitle: 'Enter a new password for your account.',
    newPasswordLabel: 'New password',
    newPasswordPlaceholder: 'Enter your new password',
    confirmPasswordLabel: 'Confirm new password',
    confirmPasswordPlaceholder: 'Repeat your new password',
    submitButton: 'Update password',
    successTitle: 'Password updated',
    successMessage: 'Your password has been changed. You can now sign in.',
    goToLogin: 'Go to sign in',
    invalidLinkTitle: 'Link expired or invalid',
    invalidLinkMessage: 'This password reset link is no longer valid. Please request a new one.',
    validationPasswordShort: 'Password must be at least 8 characters',
    validationConfirmMismatch: 'Passwords do not match',
    pwRuleMinLength: 'At least 8 characters',
    pwRuleUpper: 'One uppercase letter',
    pwRuleLower: 'One lowercase letter',
    pwRuleNumber: 'One number',
    pwRuleSymbol: 'One symbol',
    pwRuleTooLong: 'Passwords cannot be longer than 72 characters. Please shorten it.',
    missingFields: 'Still needed: {fields}',
    fieldPassword: 'password',
    fieldConfirmPassword: 'password confirmation',
    genericError: 'Something went wrong. Please try again.',
  },
  setPasswordPage: {
    title: 'Set your password',
    subtitle: 'Tell us your name and choose a password to activate your account.',
    firstNameLabel: 'First name',
    firstNamePlaceholder: 'Your first name',
    lastNameLabel: 'Last name',
    lastNamePlaceholder: 'Your last name',
    newPasswordLabel: 'Password',
    newPasswordPlaceholder: 'Enter your password',
    confirmPasswordLabel: 'Confirm password',
    confirmPasswordPlaceholder: 'Repeat your password',
    submitButton: 'Activate account',
    successTitle: 'Account activated',
    successMessage: 'Your password is set. Taking you to the workspace…',
    invalidLinkTitle: 'Invite expired or invalid',
    invalidLinkMessage: 'This invite link is no longer valid. Ask an administrator to resend it.',
    validationPasswordShort: 'Password must be at least 8 characters',
    validationConfirmMismatch: 'Passwords do not match',
    pwRuleMinLength: 'At least 8 characters',
    pwRuleUpper: 'One uppercase letter',
    pwRuleLower: 'One lowercase letter',
    pwRuleNumber: 'One number',
    pwRuleSymbol: 'One symbol',
    pwRuleTooLong: 'Passwords cannot be longer than 72 characters. Please shorten it.',
    genericError: 'Something went wrong. Please try again.',
  },
  contactPage: {
    heroTitle: "Let's Talk",
    heroSubtitle: 'Tell us about your business.',
    formTitle: 'Send us a message',
    nameLabel: 'Name',
    namePlaceholder: 'Your name',
    emailLabel: 'Email',
    emailPlaceholder: 'you@company.com',
    serviceLabel: "I'm interested in...",
    servicePlaceholder: 'Select a service',
    optionalLabel: '(optional)',
    messageLabel: 'Message',
    messagePlaceholder: 'Tell us about your project...',
    sendButton: 'Send Message',
    sending: 'Sending...',
    successTitle: 'Message Sent',
    successMessage: "We'll get back to you shortly.",
    errorMessage: 'Something went wrong. Please try again or email us directly.',
    dailyLimitMessage: 'The form is closed for today. Please try again tomorrow, or email us at hello@evtimia.com.',
    validationEmail: 'Email is required.',
    validationEmailInvalid: 'Enter a valid email.',
    validationMessage: 'Message is required.',
    serviceOptions: [
      'Social Media Subscription',
      'Automation & AI',
      'Web & Digital Platforms',
      'Strategy & Brand Identity',
      'Social Media (A-La-Carte)',
      'PPC & Paid Advertising',
      'Other / Custom Inquiry',
    ],
  },
  notFound: {
    title: '404',
    message: "This page doesn't exist.",
    backHome: 'Back to Home',
  },
  errorBoundary: {
    title: 'This page ran into a problem',
    message: 'Nothing you did caused it. Reloading usually clears it.',
    retry: 'Reload the page',
    backHome: 'Back to Home',
    contact: 'Tell us what happened',
  },
  privacyPolicy: {
    title: 'Privacy Policy (GDPR)',
    lastUpdated: 'Last updated: October 2, 2026',
    content: '', // Legal pages use raw JSX, content marker only
  },
  termsPage: {
    title: 'Terms & Conditions of Use',
    lastUpdated: 'Last updated: February 15, 2026',
    content: '',
  },
  cookiePolicy: {
    title: 'Cookie Policy',
    lastUpdated: 'Last updated: February 15, 2026',
    content: '',
  },
  workData: {
    'ghid-online': {
      name: 'Ghid.Online',
      tagline: 'Learn digital, at any age',
      description:
        'Ghid.Online is our own social enterprise platform — a free digital education library built for the people usually left behind by technology. It teaches seniors, jobseekers and anyone without a technical background how to use the internet safely, recognise misinformation, and navigate the digital services that daily life now depends on. Everything on it is free, in Romanian, and explained from the very beginning.',
      highlights: [
        'Free video courses across three tracks: general digital skills, technology, and online safety',
        'Written and filmed for absolute beginners — no jargon, no assumed knowledge',
        '"Stela", a digital assistant that recommends the right course for each visitor',
        'A short questionnaire tailors the learning path to age, location and occupation',
        'Built for seniors, jobseekers and digitally underserved communities',
        'Co-financed by the European Union and the Romanian Government under the "Start Social Enterprise in Urban Environment" programme',
      ],
      stats: [
        { value: '12', label: 'Video courses' },
        { value: '3', label: 'Learning tracks' },
        { value: '100%', label: 'Free access' },
      ],
    },
    // TODO: replace the placeholder descriptions below with real project write-ups.
    'imobiliare360': { name: 'Imobiliare360', description: '' },
    'credite360': { name: 'Credite360', description: '' },
    'asigurari360': { name: 'Asigurări360', description: '' },
    'nestor360': { name: 'Nestor360', description: '' },
    'eproprietar': { name: 'eProprietar', description: '' },
    'next-level-house': { name: 'Next Level House', description: '' },
    'exp': { name: 'eXp Realty', description: '' },
    'andreea-petcu': { name: 'Andreea Ștefania Petcu', description: '' },
    'doina-ielciu': { name: 'Doina Ielciu', description: '' },
    'sorin-sucala': { name: 'Sorin Sucală', description: '' },
  },
  serviceData: {
    'social-media-subscription': {
      title: 'Social Media Subscription',
      cardDescription: 'A complete system for strategy, content production, and management for a consistent online presence.',
      modal: {
        title: 'Consistent Content System',
        priceKey: 'smm.subscription',
        description: 'A complete subscription built for brands that want a consistent and well-organized social media presence.',
        tiers: [
          {
            name: 'Base',
            priceKey: 'smm.base',
            description: 'Consistent presence on your two most important platforms.',
            features: [
              '2 main platforms',
              '~12 posts / month',
              'No filming session included',
              'Standard community management',
              'Monthly content plan',
              'Monthly performance report',
            ],
          },
          {
            name: 'Pro',
            priceKey: 'smm.pro',
            description: 'Our published package — full platform coverage plus monthly video.',
            highlighted: true,
            badge: 'Most popular',
            features: [
              '4 main platforms',
              '20–22 posts / month',
              '1 filming session / month (2–3 hours)',
              'Standard community management',
              'Monthly content plan',
              'Weekly performance monitoring',
            ],
          },
          {
            name: 'Premium',
            priceKey: 'smm.premium',
            description: 'Extended coverage, extra production and hands-on coaching. Final price depends on selected add-ons.',
            features: [
              '4 platforms + 1 extra',
              '20–22 posts / month + extended mix',
              '1 filming session / month + additional session',
              'Extended community management',
              'Monthly content plan',
              'Weekly monitoring + monthly coaching',
            ],
          },
        ],
        sections: [
          {
            heading: 'Strategy & Planning',
            items: [
              { label: 'Tailored social media strategy' },
              { label: 'Activation of 4 main platforms (Facebook, Instagram, TikTok, LinkedIn)' },
              { label: 'Monthly content plan updated based on objectives and context' },
            ],
          },
          {
            heading: 'Consistent Content',
            items: [
              { label: 'Up to 5 posts / week (20–22 posts / month)' },
              { label: 'Monthly mix adapted to strategy (e.g. 2 static + 3 video posts per week)' },
              { label: 'Strategic cross-posting and content rotation across platforms' },
            ],
          },
          {
            heading: 'Video Production (Cluj-Napoca only)',
            items: [
              { label: '1 filming session / month (2–3 hours)' },
              { label: 'Editing for vertical formats (Reels / TikTok / Shorts)' },
              { label: 'Optional: AI-generated digital clone' },
            ],
          },
          {
            heading: 'Management & Support',
            items: [
              { label: 'Publishing via Metricool' },
              { label: 'Weekly performance monitoring' },
              { label: '1 monthly coaching session for filming preparation' },
            ],
          },
        ],
        addOns: [
          'Paid Ads campaigns',
          'Extended community management',
          'Additional filming sessions',
          'Additional platforms',
          'Additional videos',
          'Digital clone',
        ],
      },
    },
    'automation-ai': {
      title: 'Automation & AI',
      cardDescription: 'Practical and results-oriented automation to streamline your processes and save time.',
      modal: {
        title: 'Automation Services',
        description: 'We implement solutions step by step, test each workflow, and provide training so you can manage systems easily.',
        sections: [
          {
            heading: 'Available Services',
            items: [
              {
                label: 'Social Media Management Integration',
                priceKey: 'automation.social',
                details: [
                  'Connects your posting, inbox, and CRM tools',
                  'Automated scheduling and reply routing',
                  'Setup, testing, and handover included',
                ],
              },
              {
                label: 'Simple Funnel Automation',
                priceKey: 'automation.simple-funnel',
                details: [
                  'Lead capture synced straight into your CRM',
                  'Automated email and WhatsApp follow-ups',
                  'Basic reporting on each conversion step',
                ],
              },
              {
                label: 'Complex Funnel Automation',
                priceKey: 'automation.complex-funnel',
                details: [
                  'Multi-channel flows with conditional logic',
                  'Lead scoring and segmentation',
                  'Integrations across several platforms',
                ],
              },
              {
                label: 'Custom AI Agents',
                priceKey: 'automation.ai-agents',
                details: [
                  'Trained on your services and tone of voice',
                  'Handles FAQs, qualification, or internal tasks',
                  'Deployed on the channel you use most',
                ],
              },
              {
                label: 'Process Audit & Architecture',
                priceKey: 'automation.audit',
                details: [
                  'Mapping of current workflows',
                  'Identification of automation opportunities',
                  'Implementation roadmap with priorities',
                ],
              },
              {
                label: 'Automation Training',
                priceKey: 'automation.training',
                details: [
                  'Hands-on sessions with your team',
                  'Documentation for every workflow',
                  'Post-launch support for adjustments',
                ],
              },
            ],
          },
        ],
      },
    },
    'web-digital-platforms': {
      title: 'Web & Digital Platforms',
      cardDescription: 'Development of tailored websites, online stores, and internal digital tools.',
      modal: {
        title: 'Web & Digital Development (Low-Code/No-Code)',
        description: 'Solutions designed to integrate efficiently into your marketing strategy and support long-term growth.',
        sections: [
          {
            heading: 'Available Services',
            items: [
              {
                label: 'Landing Pages & Web Design',
                priceKey: 'web.landing',
                details: [
                  'Custom design aligned with your brand',
                  'Mobile-first, fast-loading build',
                  'Conversion-focused structure and CTAs',
                ],
              },
              {
                label: 'Portfolio / Presentation Website',
                priceKey: 'web.portfolio',
                details: [
                  'Up to 5 pages and 8 project entries',
                  'Contact form and basic SEO',
                  'Order online, no call needed',
                ],
              },
              {
                label: 'E-commerce — Online Stores',
                priceKey: 'web.ecommerce',
                details: [
                  'Product catalog and checkout setup',
                  'Payment and delivery integrations',
                  'Training for managing orders',
                ],
              },
              {
                label: 'Custom Digital Tools (CRM, dashboards)',
                priceKey: 'web.tools',
                details: [
                  'Internal dashboards and CRM flows',
                  'Role-based access for your team',
                  'Built around your existing processes',
                ],
              },
              {
                label: 'Website Maintenance & Optimization (SEO/AEO)',
                priceKey: 'web.maintenance',
                details: [
                  'Updates, backups, and uptime monitoring',
                  'On-page SEO/AEO improvements',
                  'Monthly performance report',
                ],
              },
              {
                label: 'Newsletter & Email Marketing Subscription',
                priceKey: 'web.newsletter',
                details: [
                  'Template design and list management',
                  'Monthly campaign planning and sending',
                  'Open, click, and conversion tracking',
                ],
              },
            ],
          },
        ],
      },
    },
    'strategy-brand-identity': {
      title: 'Strategy & Brand Identity',
      cardDescription: 'Foundational marketing strategies, daily project management, and cohesive visual identities for your business.',
      modal: {
        title: 'Strategy & Brand Identity',
        sections: [
          {
            heading: 'Project Management',
            priceKey: 'brand.pm',
            description: 'We provide daily coordination of marketing activities, with a focus on clarity, alignment, and measurable results.',
            items: [
              { label: 'Regular working meetings' },
              { label: 'Setting objectives' },
              { label: 'Monitoring KPIs and budgets' },
              { label: 'Project management for active campaigns' },
              { label: 'Results reporting' },
              { label: 'Coordination of internal/external resources' },
              { label: 'Direct involvement in ongoing optimizations' },
            ],
          },
          {
            heading: 'Marketing Strategy',
            priceKey: 'brand.strategy',
            description: 'We build marketing strategies tailored to each business.',
            items: [
              { label: 'Market and competitor analysis' },
              { label: 'Target audience & buyer personas' },
              { label: 'Brand positioning & messaging' },
              { label: 'Selection of communication channels' },
              { label: 'Detailed 6–12 month action plan' },
              { label: 'Definition of KPIs' },
              { label: 'Budget recommendations' },
            ],
          },
          {
            heading: 'Logo Ready to Use',
            priceKey: 'brand.logo',
            description: 'A functional logo built from professional templates.',
            items: [
              { label: 'Briefing' },
              { label: 'Template selection & adaptation' },
              { label: 'Color/typography customization' },
              { label: '1–2 revision rounds' },
              { label: 'Delivery in standard formats (PNG, JPG, PDF)' },
              { label: 'Monochrome versions' },
              { label: 'Basic usage guidelines' },
            ],
          },
          {
            heading: 'Visual Materials',
            description: 'Visual communication assets aligned with your brand.',
            items: [
              { label: 'Print: Business cards, flyers, brochures, catalogs, packaging, roll-ups, banners' },
              { label: 'Digital: Social media templates, presentations, email signatures, web banners, newsletter graphics' },
            ],
            priceKey: 'brand.visual',
          },
        ],
      },
    },
    'social-media-alacarte': {
      title: 'Social Media (A-La-Carte)',
      cardDescription: 'End-to-end management, content creation, and community interaction.',
      modal: {
        title: 'Standalone Social Media Services',
        sections: [
          {
            heading: 'Service Pricing',
            items: [
              {
                label: 'Digital Content Strategy',
                priceKey: 'social.strategy',
                details: [
                  'Channel and format recommendations',
                  'Content pillars and key messaging',
                  'Editorial calendar framework',
                ],
              },
              {
                label: 'Copywriting & Content Writing',
                priceKey: 'social.copywriting',
                details: [
                  'Captions, scripts, and ad copy',
                  'Consistent brand tone of voice',
                  'One revision round included',
                ],
              },
              {
                label: 'Static Posts',
                priceKey: 'social.static',
                details: [
                  'Branded graphic per post',
                  'Caption and hashtag set',
                  'Sized for each platform',
                ],
              },
              {
                label: 'Carousel Design',
                priceKey: 'social.carousel',
                details: [
                  'Up to 7 slides, sized for the platform',
                  'Editable source file included',
                  'Order online, no call needed',
                ],
              },
              {
                label: 'Video Content (Basic Editing)',
                priceKey: 'social.video-basic',
                details: [
                  'Cuts, captions, and music',
                  'Vertical format export',
                  'Delivered ready to publish',
                ],
              },
              {
                label: 'Video Content (Advanced Editing)',
                priceKey: 'social.video-advanced',
                details: [
                  'Motion graphics and transitions',
                  'Color grading and sound design',
                  'Multiple format versions',
                ],
              },
              {
                label: 'Video Content Production (Concept, filming, editing)',
                priceKey: 'social.video-production',
                details: [
                  'Concept and shot list',
                  'On-location filming',
                  'Full editing and delivery',
                ],
              },
              {
                label: 'Community Content (WhatsApp, Discord)',
                priceKey: 'social.community',
                details: [
                  'Daily moderation and replies',
                  'Conversation prompts and announcements',
                  'Monthly engagement summary',
                ],
              },
              {
                label: 'Video Content Coaching',
                priceKey: 'social.coaching',
                details: [
                  'On-camera presence and delivery',
                  'Scripting and hook techniques',
                  'Filming setup using your own phone',
                ],
              },
            ],
          },
        ],
      },
    },
    'ppc-advertising': {
      title: 'PPC & Paid Advertising',
      cardDescription: 'Turn your advertising budget into results on Meta, Google, TikTok, and LinkedIn.',
      modal: {
        title: 'PPC Campaigns',
        description: 'Custom strategy, professional setup, performance-driven creatives, and daily optimization. Recommended minimum ad budget: €1,500–€2,000/platform.',
        sections: [
          {
            heading: 'Management Fees',
            items: [
              {
                label: 'Meta (Facebook & Instagram)',
                priceKey: 'ppc.meta',
                details: [
                  'Campaign strategy and audience setup',
                  'Creatives adapted for Feed, Reels, and Stories',
                  'Daily optimization and monthly reporting',
                ],
              },
              {
                label: 'Google Ads',
                priceKey: 'ppc.google',
                details: [
                  'Search, Performance Max, and remarketing',
                  'Keyword research and negative lists',
                  'Conversion tracking and monthly reporting',
                ],
              },
              {
                label: 'TikTok Ads',
                priceKey: 'ppc.tiktok',
                details: [
                  'Native, trend-aware ad creatives',
                  'Audience testing and scaling',
                  'Daily optimization and monthly reporting',
                ],
              },
              {
                label: 'LinkedIn Ads',
                priceKey: 'ppc.linkedin',
                details: [
                  'B2B targeting by role and industry',
                  'Lead gen forms and message ads',
                  'Pipeline-focused reporting',
                ],
              },
              {
                label: 'Alternative Platforms (Reddit, X, Spotify)',
                priceKey: 'ppc.alternative',
                details: [
                  'Setup on Reddit, X, or Spotify',
                  'Creative adapted per platform',
                  'Testing budget guidance and reporting',
                ],
              },
            ],
          },
        ],
        notes: ['Advertising budgets are not included in management fees.'],
      },
    },
  },
}
